import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import EntityNameSchema from "legal-entities/entity-names/_schema/entity-name-schema";
import EntityName from "legal-entities/entity-names/entity-name";
import { z } from "zod";

export type UserOpenEntityAgreementJSON = z.infer<typeof UserOpenEntityAgreementSchema.UserOpenEntityAgreementJSONZ>;
export type UserOpenEntityAgreementParams = z.infer<typeof UserOpenEntityAgreementSchema.UserOpenEntityAgreementZ>;

export default class UserOpenEntityAgreementSchema {
  public static readonly UserOpenEntityAgreementJSONZ = z.object({
    id: z.uuid(),
    entityName: EntityNameSchema.EntityNameJSONZ,
  });

  public static readonly UserOpenEntityAgreementZ = z.object({
    id: z.instanceof(Guid),
    entityName: z.instanceof(EntityName),
  });

  public static validate(values: unknown): UserOpenEntityAgreementParams {
    return parseOrThrow(UserOpenEntityAgreementSchema.UserOpenEntityAgreementZ, values, {
      logError: true,
      schemaName: "UserOpenEntityAgreementSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): UserOpenEntityAgreementJSON {
    return parseOrThrow(UserOpenEntityAgreementSchema.UserOpenEntityAgreementJSONZ, json, {
      logError: true,
      schemaName: "UserOpenEntityAgreementSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is UserOpenEntityAgreementParams {
    try {
      UserOpenEntityAgreementSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is UserOpenEntityAgreementJSON {
    try {
      UserOpenEntityAgreementSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: UserOpenEntityAgreementJSON): UserOpenEntityAgreementParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(UserOpenEntityAgreementSchema.UserOpenEntityAgreementJSONZ, json, {
      logError: true,
      schemaName: "UserOpenEntityAgreementSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return UserOpenEntityAgreementSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: UserOpenEntityAgreementParams): UserOpenEntityAgreementJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = UserOpenEntityAgreementSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return UserOpenEntityAgreementSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    UserOpenEntityAgreementSchema.UserOpenEntityAgreementJSONZ,
    UserOpenEntityAgreementSchema.UserOpenEntityAgreementZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        entityName: new EntityName(EntityNameSchema.deserialize(data.entityName)),
      }),
      encode: (agreement) => ({
        id: agreement.id.toJSON(),
        entityName: agreement.entityName.toJSON(),
      }),
    }
  );
}
